import React, { useCallback, useEffect, useState } from 'react';
import { Link, useHistory, useLocation, useParams } from 'react-router-dom';
import * as Yup from 'yup';
import { doc, setDoc, getDoc, updateDoc } from 'firebase/firestore';
import { v4 } from 'uuid';

import { format } from 'date-fns';
import { DB } from '~/services/firebase';
import { useAuth } from '~/hooks/auth';

import Dashboard from '~/layouts/Dashboard';
import getValidationErrors from '~/utils/getValidationErrors';
import { useToast } from '~/hooks/toast';

interface FormData {
  uid: string;
  name: string;
  value: number;
  date: string;
  type: string;
}

interface Params {
  id: string;
}

const CashFlowForm: React.FC = () => {
  const history = useHistory();
  const params = useParams<Params>();
  const { state } = useLocation<any>();
  const { user, company } = useAuth();
  const { addToast } = useToast();

  const [data, setData] = useState({} as FormData);
  const [errors, setErrors] = useState({} as any);
  const [loading, setLoading] = useState(false);
  const { id } = params;

  useEffect(() => {
    setTimeout(() => {
      ($('.money') as any).mask('#.##0,00', { reverse: true });
    }, 500);

    async function loadCashFlow() {
      const docSnap = await getDoc(doc(DB, 'cashflow', id));
      setData({
        ...docSnap.data(),
        uid: id,
      } as FormData);
    }

    if (state) {
      setData({
        ...state,
        date: state.date_formatted,
      } as FormData);
    } else if (id) {
      loadCashFlow();
    } else {
      setData({
        date: format(new Date(), 'yyyy-MM-dd HH:mm'),
        type: '0',
      } as FormData);
    }
  }, [id, state]);

  const handleSubmit = useCallback(
    async event => {
      event.preventDefault();

      try {
        let initValue = (document.getElementById('value') as any)
          .value as string;
        initValue = initValue.replaceAll('.', '');
        initValue = initValue.replaceAll(',', '.');

        const validateData = {
          ...data,
          value: initValue,
        };

        const schema = Yup.object().shape({
          name: Yup.string().required('O nome do lançamento é obrigatório'),
          date: Yup.string().required('A data do lançamento é obrigatória'),
          value: Yup.number().required('O valor do lançamento é obrigatório'),
        });

        await schema.validate(validateData, {
          abortEarly: false,
        });

        setLoading(true);

        const { name, date, value } = validateData;

        const parseDate = new Date(date);

        const uid = validateData.uid || v4();

        const parseData: any = {
          ...validateData,
          uid,
          name,
          date: parseDate,
          value,
          user_id: user.uid,
          company_id: company.uid,
        };

        if (validateData.uid) {
          await updateDoc(doc(DB, 'cashflow', uid), parseData);
        } else {
          await setDoc(doc(DB, 'cashflow', uid), parseData);
        }

        addToast({
          type: 'success',
          description: data.uid
            ? 'Lançamento alterado com sucesso!'
            : 'Lançamento criado com sucesso!',
        });

        history.push('/resume');

        setLoading(false);
      } catch (err: any) {
        setLoading(false);
        if (err instanceof Yup.ValidationError) {
          const parseErrors = getValidationErrors(err);
          setErrors(parseErrors);
        }
      }
    },
    [user, company, data, addToast, history],
  );

  return (
    <Dashboard containerMessage="col-xl-6 offset-xl-3 col-md-10 offset-md-1">
      <div className="row">
        <div className="col-xl-6 offset-xl-3 col-md-10 offset-md-1">
          <div className="nk-block nk-block-lg">
            <div className="nk-block-head">
              <div className="nk-block-head-content">
                <div className="nk-block-head-sub">
                  <Link className="back-to" to="/resume">
                    <em className="icon ni ni-arrow-left" />
                    <span>Voltar</span>
                  </Link>
                </div>
                <h4 className="title nk-block-title">
                  {id ? 'Editar Lançamento' : 'Criar Lançamento'}
                </h4>
                <div className="nk-block-des">
                  <p>
                    Preencha o formulário abaixo e crie um novo lançamentode
                    fluxo de caixa.
                  </p>
                </div>
              </div>
            </div>
            <div className="card card-bordered">
              <div className="card-inner">
                <form onSubmit={handleSubmit}>
                  <div className="row g-2">
                    <div className="col-lg-6">
                      <div className="form-group">
                        <label className="form-label">Nome do Lançameto</label>
                        <div className="form-control-wrap">
                          <input
                            type="text"
                            className="form-control"
                            defaultValue={data.name}
                            onChange={event => {
                              setData({ ...data, name: event.target.value });
                            }}
                          />
                          {errors.name && (
                            <span className="invalid">{errors.name}</span>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="col-lg-6">
                      <div className="form-group">
                        <label className="form-label">Valor</label>
                        <div className="form-control-wrap">
                          <input
                            id="value"
                            type="text"
                            className="form-control money"
                            defaultValue={data.value}
                            onChange={event => {
                              setData({
                                ...data,
                                value: parseFloat(event.target.value),
                              });
                            }}
                          />
                          {errors.value && (
                            <span className="invalid">{errors.value}</span>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="col-lg-6">
                      <div className="form-group mb-3">
                        <label className="form-label">Data</label>
                        <div className="form-control-wrap">
                          <input
                            type="datetime-local"
                            className="form-control"
                            defaultValue={data.date}
                            onChange={event => {
                              setData({
                                ...data,
                                date: event.target.value,
                              });
                            }}
                          />
                          {errors.date && (
                            <span className="invalid">{errors.date}</span>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="col-sm-6">
                      <div className="form-group mb-3">
                        <label className="form-label" htmlFor="type">
                          Tipo de Lançamento
                        </label>
                        <div className="form-control-wrap ">
                          <div className="form-control-select">
                            <select
                              className="form-control"
                              id="type"
                              value={data.type}
                              onChange={event => {
                                setData({
                                  ...data,
                                  type: event.target.value,
                                });
                              }}
                            >
                              <option value="0">Saída</option>
                              <option value="1">Entrada</option>
                            </select>
                            {errors.type && (
                              <span className="invalid">{errors.type}</span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="col-12">
                      <div className="form-group">
                        <button
                          type="submit"
                          className="btn btn-lg btn-primary"
                          disabled={loading}
                        >
                          {loading && (
                            <div
                              className="spinner-border spinner-border-sm"
                              role="status"
                            />
                          )}
                          {!loading && 'Salvar'}
                        </button>
                      </div>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Dashboard>
  );
};

export default CashFlowForm;
